import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getStationTitleWithTownName } from '../../../utils/StationUtils'
import useTitle from '../../../utils/customHook/useTitle'
import HydrometryAction from '../../actions/HydrometryAction'
import { last, minBy, range, round } from 'lodash'
import moment from 'moment/moment'
import HydroSuiviTable from './HydroSuiviTable'
import HydroSuiviCriterias from './HydroSuiviCriterias'
import PropTypes from 'prop-types'
import ProgressCard from '../../../components/card/ProgressCard'
import { Grid } from '@mui/material'
import DtoHydrometricStation from '../../dto/DtoHydrometricStation'
import DtoHydroStats from '../../dto/chronicMeasures/DtoHydroStats'
import { HYDRO_MEASURE_COTE } from '../../constants/HydrometryConstants'
import { hasValue } from '../../../utils/NumberUtil'

const HydroSuiviTableApp = ({}) => {
    const {
        hydrometricStation,
        hydroStatistics,
        hydrometricStations,
        citiesIndex,
    } = useSelector(store => ({
        hydrometricStation: store.HydrometryReducer.hydrometricStation,
        hydroStatistics: store.HydrometryReducer.hydroStatistics,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [dataLoaded, setDataLoaded] = useState(false)
    const [measures, setMeasures] = useState([])
    const [years, setYears] = useState([])
    const [filter, setFilter] = useState({ group: 'MAX', horizon: 'year' })
    const currentDataType = filter.dataType ?? (hydroStatistics.some(h => h.typeId === 4) ? 4 : (hydroStatistics[0]?.typeId || 4))

    const townName = useMemo(() => citiesIndex[hydrometricStation.townCode]?.name, [citiesIndex, hydrometricStation])

    useTitle(() => [{
        title: i18n.hydrometry,
        href: 'hydrometry',
    }, {
        title: getStationTitleWithTownName(hydrometricStation, townName),
        href: `station/hydrometry/${hydrometricStation.id}`,
    }, {
        title: i18n.followUpTable,
        href: `station/hydrometry/${hydrometricStation.id}/hydroSuiviTable`,
    }], [hydrometricStation, townName])

    const reloadMeasures = (tmpFilter, usedYear = filter.year) => {
        setDataLoaded(false)
        setFilter(tmpFilter)
        const startDate = moment().utcOffset(+1).year(usedYear).startOf('year').valueOf()
        const endDate= moment().utcOffset(+1).year(usedYear).endOf('year').valueOf()
        HydrometryAction.promiseHydroChronicMeasuresLight({
            stationId: hydrometricStation.id,
            dataType: tmpFilter.dataType ?? currentDataType,
            groupFunc: tmpFilter.group,
            ...(tmpFilter.horizon === 'year' ? {
                startDate,
                endDate,
            } : {}),
            chartMode: true,
            // displayCote -> getMeasuresByReference()
        }).then(json => {
            const filteredStatus = tmpFilter.status ? json.filter(s => s.status === tmpFilter.status) : json
            const filteredQualification = tmpFilter.qualification ? filteredStatus.filter(s => s.qualification === tmpFilter.qualification) : filteredStatus
            setMeasures(filteredQualification)
            setDataLoaded(true)
        })
    }

    useEffect(() => {
        if (hydroStatistics.length) {
            const minDate = minBy(hydroStatistics, 'startDate')?.startDate
            const calculatedYears = range(moment().year(), parseInt(moment(minDate).year()) - 1).map(y => ({ code: y, name: y }))
            setYears(calculatedYears)
            reloadMeasures({ ...filter, year: calculatedYears[0].code }, calculatedYears[0].code)
        }
    }, [hydroStatistics])

    useEffect(() => {
        if (!hydroStatistics.length) {
            dispatch(HydrometryAction.fetchHydroStatistics(hydrometricStation.id)).then(res => {
                if (!res.length) {
                    setDataLoaded(true)
                }
            })
        }
    }, [])

    const horizon = useMemo(() => {
        return filter.horizon
    }, [measures])

    const getMeasuresByReference = (m) => {
        if (filter.displayCote === HYDRO_MEASURE_COTE.NGF) {
            const landmark = hydrometricStation.link_landmarks[0]
            if (landmark) {
                const altimetricSystem = hydrometricStation.link_altimetrySystems?.find(a => a.natureCode === landmark.altimetrySystemNature && a.startDate === landmark.altimetrySystemDate)
                const rating = (hasValue(altimetricSystem) && hasValue(landmark.height)) && round(altimetricSystem.altitude + landmark.height, 2)
                return rating ? m.map(hmm => ({ ...hmm, value: hmm.value + rating })) : m
            }
            return m
        }
        return m
    }

    return (
        <Grid container size={12} padding={1} spacing={1}>
            { filter.year && (
                <HydroSuiviCriterias
                    hydrometricStation={hydrometricStation}
                    defaultFilter={filter}
                    hydroStatistics={hydroStatistics}
                    reloadMeasures={reloadMeasures}
                    years={years}
                />)
            }
            <Grid size={12} sx={{ height: '75vh' }}>
                {!dataLoaded ? <ProgressCard progress={0}/> :
                    <HydroSuiviTable
                        hydrometricStation={hydrometricStation}
                        hydrometricStations={hydrometricStations}
                        measures={getMeasuresByReference(measures)}
                        dataType={hydroStatistics.find(h => h.typeId === currentDataType)}
                        horizon={horizon}
                        dataLoaded={dataLoaded}
                        minYear={last(years)?.code}
                        maxYear={years[0]?.code}
                        selectedYear={filter.year}
                    />
                }
            </Grid>
        </Grid>
    )
}


HydroSuiviTableApp.propTypes = {
    hydrometricStation: PropTypes.instanceOf(DtoHydrometricStation),
    hydroStatistics: PropTypes.arrayOf(DtoHydroStats),
    hydrometricStations: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
    typeEnvironmentModels: PropTypes.arrayOf(PropTypes.string),
}

export default HydroSuiviTableApp
